import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { usePartners } from '../lib/usePublicData';
import { PARTNER_FORMULAS } from '../data/partnerFormulas';

const sizeMap = { ZOMERKICK: 'kick', ZOMERBOOST: 'boost', ZOMERVIBES: 'vibes', ZOMERGOESTING: 'goesting' };
const formulaIdToKey = { zomerkick: 'ZOMERKICK', zomerboost: 'ZOMERBOOST', zomervibes: 'ZOMERVIBES', zomergoesting: 'ZOMERGOESTING' };

const PartnerCard = ({ partner, size, formulaName }) => {
    // Card-size hierarchy: ZOMERKICK > BOOST > VIBES > GOESTING.
    // Logo heights are bumped up significantly so the logo fills the visual area.
    const sizeClasses = {
        kick:     { card: 'min-h-[200px] sm:min-h-[240px]', img: 'max-h-32 sm:max-h-40 md:max-h-48', text: 'text-2xl sm:text-3xl' },
        boost:    { card: 'min-h-[170px] sm:min-h-[200px]', img: 'max-h-28 sm:max-h-36 md:max-h-40', text: 'text-xl sm:text-2xl' },
        vibes:    { card: 'min-h-[140px] sm:min-h-[170px]', img: 'max-h-24 sm:max-h-28 md:max-h-32', text: 'text-lg sm:text-xl' },
        goesting: { card: 'min-h-[120px] sm:min-h-[140px]', img: 'max-h-20 sm:max-h-24',             text: 'text-base sm:text-lg' },
    }[size] || { card: 'min-h-[140px]', img: 'max-h-24', text: 'text-lg' };

    const isPremium = size === 'kick';
    const href = partner.website_url || partner.website || null;
    const Wrap = href ? 'a' : 'div';
    const wrapProps = href ? {
        href,
        target: '_blank',
        rel: 'noopener noreferrer',
        'aria-label': `${partner.name} website (opent in nieuw tabblad)`,
    } : { 'aria-label': partner.name };

    let logoSrc = partner.logo_url || '';
    // /api/ paths blijven relatief (browser resolved tegen huidige origin)
    // Geen BACKEND-prefix → deploy-safe ongeacht REACT_APP_BACKEND_URL waarde.

    // Graceful fallback: als de logo-file 404 gooit (bv. verloren uploads bij
    // ephemeral storage redeploy), tonen we automatisch de partner-naam in Bungee
    // in plaats van een broken-image icon.
    const [logoError, setLogoError] = useState(false);
    useEffect(() => { setLogoError(false); }, [logoSrc]);
    const showLogoImage = !!logoSrc && !logoError;

    return (
        <Wrap
            {...wrapProps}
            data-testid={`partner-card-${partner.id}`}
            className={`group flex items-center justify-center text-center p-3 sm:p-4 ${
                isPremium ? 'zz-card-premium' : 'zz-card-soft'
            } ${sizeClasses.card}`}
        >
            {showLogoImage ? (
                <img
                    src={logoSrc}
                    alt={`Logo ${partner.name}`}
                    loading="lazy"
                    decoding="async"
                    onError={() => setLogoError(true)}
                    className={`${sizeClasses.img} w-auto object-contain`}
                />
            ) : (
                <span className={`zz-outline-title-dark font-display leading-tight ${sizeClasses.text}`}>
                    {partner.name}
                </span>
            )}
        </Wrap>
    );
};

const FORMULA_BG = {
    zomerkick: 'var(--lilac)',
    zomerboost: 'var(--yellow-soft)',
    zomervibes: 'var(--mint-soft)',
    zomergoesting: 'var(--pink-soft)',
};

const FormulaPlaceholderCard = ({ formula }) => (
    <div
        data-testid={`partners-placeholder-${formula.id}`}
        className="col-span-full flex flex-col items-center justify-center gap-3 p-8 sm:p-10 rounded-3xl border-2 border-dashed border-[var(--green)]/40 text-center"
        style={{ backgroundColor: FORMULA_BG[formula.id] || 'var(--mint-soft)' }}
    >
        <p className="font-display text-base sm:text-lg text-[var(--green-dark)]">
            Word partner in deze formule
        </p>
        <p className="text-xs sm:text-sm text-[var(--green-dark)]/70 max-w-md">
            Plek vrij — wees de eerste die hier zichtbaar wordt.
        </p>
        <Link
            to="/#brochure"
            data-testid={`partners-placeholder-cta-${formula.id}`}
            className="zz-btn-primary zz-pulse-cta mt-2 inline-flex items-center gap-2 !text-xs sm:!text-sm"
        >
            Vraag brochure aan
            <ArrowRight size={14} />
        </Link>
    </div>
);

const FormulaGroup = ({ formula, partners }) => {
    const list = partners.filter((p) => {
        const f = (p.formula || '').toUpperCase();
        return f === formulaIdToKey[formula.id] || f === formula.id.toUpperCase();
    });

    const gridMap = {
        zomerkick: 'grid-cols-1 md:grid-cols-3',
        zomerboost: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
        zomervibes: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4',
        zomergoesting: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6',
    };

    return (
        <div data-testid={`partners-group-${formula.id}`} className="mb-12 sm:mb-16">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
                <h3 className="font-display zz-outline-title-dark"
                    style={{ fontSize: 'clamp(1.5rem, 3.5vw, 2.25rem)' }}>
                    {formula.name}
                </h3>
                <Link
                    to={`/#brochure?formule=${formula.id}`}
                    data-testid={`partners-formula-cta-${formula.id}`}
                    aria-label={`Vraag brochure aan voor ${formula.name}`}
                    className="zz-formula-chip zz-pulse-cta"
                >
                    Vraag brochure aan
                    <ArrowRight size={13} />
                </Link>
            </div>
            <div className={`grid ${gridMap[formula.id]} gap-4 sm:gap-6`}>
                {list.length > 0 ? (
                    list.map((p) => (
                        <PartnerCard key={p.id} partner={p} size={sizeMap[formulaIdToKey[formula.id]]} formulaName={formula.name} />
                    ))
                ) : (
                    <FormulaPlaceholderCard formula={formula} />
                )}
            </div>
        </div>
    );
};

export const Partners = () => {
    const { data: partners } = usePartners();

    return (
        <section id="partners" data-testid="partners-section" className="relative py-16 sm:py-24 bg-cream">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <div className="text-center mb-10 sm:mb-14">
                    <h2 data-testid="partners-title" className="zz-outline-title font-display"
                        style={{ fontSize: 'clamp(2.25rem, 6vw, 4rem)' }}>
                        ONZE PARTNERS
                    </h2>
                    <p className="mt-4 text-sm sm:text-base text-[#006b44]/80 max-w-2xl mx-auto">
                        Dankzij onze partners blijft Zotte Zomer gratis toegankelijk voor iedereen.
                    </p>
                </div>

                {PARTNER_FORMULAS.map((f) => (
                    <FormulaGroup key={f.id} formula={f} partners={partners} />
                ))}
            </div>
        </section>
    );
};

export default Partners;
